import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { SectionTitle } from '../help';
import { useProject } from '../../context/ProjectContext';
import { applyFiltersToUrl } from '../../utils/filter-url-sync';
import {
  formatSourceCount,
  formatSourcePercent,
} from '../../utils/source-insights';
import { buildDateRangeQueryOptions } from '../../utils/visibility-range';
import type { SourceInsightsDomainsResult, SourceInsightsSummary } from '../../types';
import DataLoadError from '@/components/errors/DataLoadError';
import { DashboardSourcesBodySkeleton } from '../common/Skeleton';
import { DashboardEmptyNote } from './DashboardEmptyNote';
import { SourceMiniList } from '../sources/SourceMiniList';
import { trackerAiPath } from '../../utils/paths';
import useDomain from '@/hooks/useDomain';

export function useTrackerAiSourcesLink() {
  const {
    topicFilterId,
    modelFilterId,
    visibilityRange,
    customFrom,
    customTo,
    sourceFilterIds,
    domainFilterId,
  } = useProject();
  const [searchParams] = useSearchParams();
  const { domain_slug, domain } = useDomain();

  return useMemo(() => {
    const next = applyFiltersToUrl(searchParams, {
      visibilityRange,
      customFrom,
      customTo,
      topicFilterId,
      modelFilterId,
      domainFilterId,
      sourceFilterIds,
    });
    const query = next.toString();
    return trackerAiPath('sources', domain_slug, domain, query);
  }, [
    searchParams,
    visibilityRange,
    customFrom,
    customTo,
    topicFilterId,
    modelFilterId,
    domainFilterId,
    sourceFilterIds,
    domain_slug,
    domain,
  ]);
}

export function DashboardSourcesWidget({ hideHeader = false }: { hideHeader?: boolean }) {
  const {
    projectId,
    topicFilterId,
    modelFilterId,
    visibilityRange,
    customFrom,
    customTo,
    sourceFilterIds,
  } = useProject();
  const sourcesLink = useTrackerAiSourcesLink();
  const [summary, setSummary] = useState<SourceInsightsSummary | null>(null);
  const [domains, setDomains] = useState<SourceInsightsDomainsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const rangeQuery = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        topicId: topicFilterId ?? undefined,
        providerIds: modelFilterId ? [modelFilterId] : undefined,
        sourceIds: sourceFilterIds.length > 0 ? sourceFilterIds : undefined,
      }),
    [
      visibilityRange,
      customFrom,
      customTo,
      topicFilterId,
      modelFilterId,
      sourceFilterIds,
    ],
  );

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);

    Promise.all([
      api.getSourceInsightsSummary(projectId, rangeQuery),
      api.getSourceInsightsDomains(projectId, { ...rangeQuery, page: 1, perPage: 5 }),
    ])
      .then(([summaryData, domainsData]) => {
        if (!cancelled) {
          setSummary(summaryData as SourceInsightsSummary);
          setDomains(domainsData as SourceInsightsDomainsResult);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSummary(null);
          setDomains(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, rangeQuery, retryNonce]);

  return (
    <Card className="shadow-sm">
      <Card.Body>
        {!hideHeader ? (
          <div className="mb-3">
            <SectionTitle
              title="منابع AI"
              subtitle="دامنه‌هایی که در پاسخ‌های AI به عنوان منبع دیده شده‌اند"
              action={
                <Link to={sourcesLink} className="fs--2 fw-medium text-decoration-none" style={{ color: 'var(--falcon-navbar-vertical-default-link-active-color)' }}>
                  مشاهده همه منابع ←
                </Link>
              }
            />
          </div>
        ) : null}

        {error ? (
          <DataLoadError
            compact
            onRetry={() => setRetryNonce(n => n + 1)}
            isRetrying={loading}
          />
        ) : loading ? (
          <DashboardSourcesBodySkeleton />
        ) : !summary || summary.totalSources === 0 ? (
          <DashboardEmptyNote minHeight={160}>
            در این بازه منبعی در پاسخ‌های AI دیده نشده.
          </DashboardEmptyNote>
        ) : (
          <>
            <Row className="mb-3 g-3">
              <Col xs={4}>
                <div className="rounded-3 border bg-body-tertiary px-3 py-2 h-100">
                  <p className="fs--2 text-500 mb-0">منابع شناسایی‌شده</p>
                  <p className="mt-1 fs-0 fw-bold text-1000 mb-0" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatSourceCount(summary.totalSources)}
                  </p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="rounded-3 border bg-body-tertiary px-3 py-2 h-100">
                  <p className="fs--2 text-500 mb-0">کل Citationها</p>
                  <p className="mt-1 fs-0 fw-bold text-1000 mb-0" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatSourceCount(summary.totalCitations)}
                  </p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="rounded-3 border bg-body-tertiary px-3 py-2 h-100">
                  <p className="fs--2 text-500 mb-0">پوشش منبع</p>
                  <p className="mt-1 fs-0 fw-bold text-1000 mb-0" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatSourcePercent(summary.sourceCoverage)}
                  </p>
                </div>
              </Col>
            </Row>
            <SourceMiniList items={domains?.rows ?? []} />
          </>
        )}
      </Card.Body>
    </Card>
  );
}
